import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { featuredWorkOrFallback, FeaturedWorkViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { client } from "../client.js";
import { useAppListings } from "../apps/useAppListings.js";
import { HeroSiteDeck } from "./HeroSiteDeck.js";
import { heroAppItems, heroDeckFromFeatured } from "./heroDeckItems.js";
import { LANDING_WORK, type LandingWorkItem } from "./landingWork.js";

/**
 * LandingPage is the Lumora marketing storefront. Presentational View only:
 * value proposition, capability chapters, and real client work.
 */

interface Feature {
  title: string;
  text: string;
}

const HIGHLIGHTS: Feature[] = [
  {
    title: "One system, every location",
    text: "Menus, orders, and updates live in one place—so when something improves, every site you run gets it.",
  },
  {
    title: "A site tailored to how you sell",
    text: "We shape the look and flow around your brand and how customers buy—not a generic template you have to live with.",
  },
  {
    title: "Card payments that keep more in your pocket",
    text: "Take orders online at normal card rates instead of marketplace cut. Checkout, subscriptions, and pay-in-store—you stay the merchant of record.",
  },
  {
    title: "Your own web address",
    text: "Launch on yourname.com with secure HTTPS, or start on a Lumora link in minutes while you decide.",
  },
  {
    title: "Your business stays private",
    text: "Orders, customers, and content stay on your site alone. Nothing mixes with another business on the platform.",
  },
  {
    title: "Pay for what you use",
    text: "A clear build fee up front, then hosting that tracks real traffic—no bloated monthly plan for features you never touch.",
  },
  {
    title: "Payouts handled for you",
    text: "We set up a Stripe-managed account so identity checks, tax forms, and automatic bank deposits are taken care of—no payment-processor homework on your end.",
  },
];

const PERSONAL_SITE = "https://bahaa.lumorasites.com/";
const GITHUB = "https://github.com/bahaaaldin214";
const LINKEDIN = "https://www.linkedin.com/in/bahmohammad/";

function mapRange(value: number, inA: number, inB: number, outA: number, outB: number) {
  const t = Math.min(1, Math.max(0, (value - inA) / (inB - inA || 1)));
  return outA + (outB - outA) * t;
}

export function LandingPage() {
  const heroVisualRef = useRef<HTMLDivElement>(null);
  const dampRef = useRef(0);
  const targetRef = useRef(0);
  const rafRef = useRef(0);
  const featuredVM = useMemo(() => new FeaturedWorkViewModel(client), []);
  const featured = useViewModel(featuredVM);
  const apps = useAppListings();

  useEffect(() => {
    void featuredVM.load();
  }, [featuredVM]);

  const apiCards: LandingWorkItem[] | null = featured.items
    ? featured.items.map((item) => ({
        slug: item.slug,
        name: item.name,
        tagline: item.tagline,
        blurb: item.blurb,
        tags: item.tags ?? [],
        href: item.href,
        shotSrc: item.shotSrc,
        shotAlt: item.shotAlt || item.name,
      }))
    : null;
  const work = featuredWorkOrFallback(apiCards, LANDING_WORK);
  /** Deck queue: unique featured sites plus app windows, sorted by slug, no repeats. */
  const deck = heroDeckFromFeatured([...work, ...heroAppItems(apps.listings)]);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const onScroll = () => {
      const max = Math.min(window.innerHeight * 0.85, 720);
      targetRef.current = mapRange(window.scrollY, 0, max, 0, 1);
    };

    const tick = () => {
      dampRef.current += (targetRef.current - dampRef.current) * 0.12;
      const el = heroVisualRef.current;
      const stackedHero =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 860px)").matches;
      if (el) {
        if (stackedHero) {
          el.style.transform = "";
        } else {
          const ty = mapRange(dampRef.current, 0, 1, 0, -56);
          const scale = mapRange(dampRef.current, 0, 1, 1, 0.96);
          el.style.transform = `translate3d(0, ${ty}px, 0) scale(${scale})`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="lm">
      <section className="lm-hero lm-hero-flagship" aria-label="Lumora">
        <div className="lm-hero-inner">
          <div className="lm-hero-copy">
            <h1 className="lm-hero-brand">Lumora</h1>
            <p className="lm-hero-title">One backend. Many websites.</p>
            <p className="lm-hero-sub">
              Beautiful sites for real businesses: menus, storefronts, bookings on a shared backend
              you never manage.
            </p>
            <div className="lm-hero-cta">
              <Link to="/pricing" className="lm-link-cta">
                See pricing <span aria-hidden="true">&rsaquo;</span>
              </Link>
              <Link to="/get-started" className="lm-link-cta lm-link-cta-muted">
                Start a project <span aria-hidden="true">&rsaquo;</span>
              </Link>
            </div>
          </div>

          <div className="lm-hero-visual" ref={heroVisualRef}>
            <HeroSiteDeck items={deck} />
          </div>
        </div>
      </section>

      <section className="lm-section lm-section-soft" aria-labelledby="lm-highlights-heading">
        <div className="lm-container">
          <h2 id="lm-highlights-heading" className="lm-h2">
            Everything a small business needs to sell online
          </h2>
          <p className="lm-lead">
            Online ordering, payments, and a site that looks like you—handled once, then ready
            whenever you open the doors.
          </p>
          <div className="lm-bento">
            {HIGHLIGHTS.map((f, i) => (
              <article key={f.title} className={`lm-bento-item lm-bento-${i + 1}`}>
                <h3 className="lm-bento-title">{f.title}</h3>
                <p className="lm-bento-text">{f.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lm-chapter" aria-labelledby="lm-updates-heading">
        <div className="lm-container lm-chapter-inner">
          <p className="lm-chapter-eyebrow">Always current</p>
          <h2 id="lm-updates-heading" className="lm-chapter-title">
            Your site stays modern
          </h2>
          <p className="lm-chapter-accent">For free!</p>
          <p className="lm-chapter-body">
            With Growth+ subscriptions, visual polish reaches every site owner&rsquo;s storefront.
            Stay subscribed and your customers always see a current site. No tickets. No rebuild
            quotes.
          </p>
          <Link to="/pricing" className="lm-link-cta">
            See Growth+ on pricing <span aria-hidden="true">&rsaquo;</span>
          </Link>
        </div>
      </section>

      <section className="lm-chapter lm-chapter-soft">
        <div className="lm-container lm-chapter-inner">
          <p className="lm-chapter-eyebrow">Payments</p>
          <h2 className="lm-chapter-title">
            Lower card fees on every online order. You stay the merchant of record.
          </h2>
          <p className="lm-chapter-body">
            Sell direct on your own site at standard card rates—not the cut delivery apps and
            marketplaces take. Checkout, subscriptions, and pay-at-restaurant, wired once on the
            shared backend and ready on every site we ship.
          </p>
          <Link to="/pricing" className="lm-link-cta">
            See pricing <span aria-hidden="true">&rsaquo;</span>
          </Link>
        </div>
      </section>

      <section className="lm-chapter lm-chapter-dark">
        <div className="lm-container lm-chapter-inner">
          <p className="lm-chapter-eyebrow">Are you a developer?</p>
          <h2 className="lm-chapter-title">Every site is its own tenant.</h2>
          <p className="lm-chapter-body">
            Data never crosses boundaries. Isolation is enforced on every row, the same guarantee
            whether you run one site or twenty.
          </p>
        </div>
      </section>

      <section className="lm-section" id="work">
        <div className="lm-container">
          <h2 className="lm-h2">Our work</h2>
          <p className="lm-lead">Real sites running on Lumora today.</p>
          <div className="lm-work">
            {work.map((w) => (
              <a
                key={w.name}
                className="lm-work-card"
                href={w.href}
                target="_blank"
                rel="noreferrer"
              >
                <div className="lm-work-shot-wrap">
                  <img
                    className="lm-work-shot"
                    src={w.shotSrc}
                    alt={w.shotAlt}
                    width={640}
                    height={400}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="lm-work-body">
                  <h3 className="lm-work-name">{w.name}</h3>
                  <p className="lm-work-tagline">{w.tagline}</p>
                  <p className="lm-work-blurb">{w.blurb}</p>
                  <ul className="lm-tags">
                    {w.tags.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                  <span className="lm-work-link">Visit site &rsaquo;</span>
                </div>
              </a>
            ))}
            <div className="lm-work-card lm-work-next">
              <div className="lm-work-body">
                <h3 className="lm-work-name">Your business next</h3>
                <p className="lm-work-blurb">
                  Restaurant, dealership, studio, shop: if it sells, it can run on Lumora.
                </p>
                <Link to="/get-started" className="lm-work-link">
                  Let&rsquo;s build it &rsaquo;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lm-cta">
        <div className="lm-container lm-cta-inner">
          <div>
            <h2 className="lm-cta-title">Want a site like these?</h2>
            <p className="lm-cta-sub">
              See pricing or tell us about your project. Most sites launch in days, not months.
            </p>
          </div>
          <div className="lm-hero-cta">
            <Link to="/pricing" className="lm-btn lm-btn-primary">
              See pricing
            </Link>
            <Link to="/get-started" className="lm-btn lm-btn-ghost lm-btn-on-dark">
              Start a project
            </Link>
          </div>
        </div>
      </section>

      <footer className="lm-footer">
        <div className="lm-container lm-footer-inner">
          <div className="lm-footer-brand">
            <span className="lm-footer-logo">Lumora</span>
            <p className="lm-footer-blurb">One backend, many beautiful websites.</p>
          </div>
          <nav className="lm-footer-col" aria-label="Product">
            <p className="lm-footer-col-title">Product</p>
            <Link to="/pricing">Pricing</Link>
            <Link to="/pricing">Hosting</Link>
            <Link to="/apps">Apps</Link>
            <Link to="/developers">API</Link>
          </nav>
          <nav className="lm-footer-col" aria-label="Company">
            <p className="lm-footer-col-title">Company</p>
            <Link to="/contact">Contact</Link>
            <Link to="/terms">Service terms</Link>
            <Link to="/signin">Owner sign in</Link>
          </nav>
          <div className="lm-footer-built">
            <p className="lm-footer-col-title">Built by</p>
            <p>
              <a href={PERSONAL_SITE} target="_blank" rel="noreferrer">
                Bahaa Mohammad
              </a>
              , systems &amp; biomedical engineer.
            </p>
            <p className="lm-footer-links">
              <a href={PERSONAL_SITE} target="_blank" rel="noreferrer">
                Portfolio
              </a>
              <a href={GITHUB} target="_blank" rel="noreferrer">
                GitHub
              </a>
              <a href={LINKEDIN} target="_blank" rel="noreferrer">
                LinkedIn
              </a>
            </p>
          </div>
        </div>
        <div className="lm-footer-legal">
          <div className="lm-container">
            <p>&copy; {new Date().getFullYear()} Lumora. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
